import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

import { CheckCircle, Users, Globe, Target, TrendingUp, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1523495225575-104bd7a11999?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxpbnRlcm5hdGlvbmFsJTIwZ2xvYmFsfGVufDB8fHx8MTc2OTAzMjg0N3ww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#003f6d]/95 to-[#003f6d]/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-white mb-6">About QSC America</h1>
          <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
          <p className="text-2xl text-gray-200 max-w-3xl">
            Your trusted partner in ISO certification, dedicated to helping organizations achieve excellence through internationally recognized standards.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Card className="border-t-4 border-[#003f6d]">
              <CardContent className="p-8">
                <Target className="w-12 h-12 text-[#003f6d] mb-4" />
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  To empower organizations worldwide to achieve operational excellence through rigorous, fair, and value-added ISO certification services. We are committed to helping our clients build robust management systems that drive continuous improvement and sustainable success.
                </p>
              </CardContent>
            </Card>
            <Card className="border-t-4 border-[#d4af37]">
              <CardContent className="p-8">
                <TrendingUp className="w-12 h-12 text-[#d4af37] mb-4" />
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  To be the most trusted and respected ISO certification body globally, recognized for our expertise, integrity, and unwavering commitment to quality. We envision a world where every organization, regardless of size or industry, has access to world-class certification services.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: CheckCircle,
                title: 'Integrity',
                description: 'We conduct all audits with impartiality, honesty, and the highest ethical standards.'
              },
              {
                icon: Users,
                title: 'Excellence',
                description: 'We strive for excellence in every aspect of our certification services.'
              },
              {
                icon: Globe,
                title: 'Global Reach',
                description: 'We serve clients worldwide with consistent quality and local expertise.'
              },
              {
                icon: Target,
                title: 'Client Focus',
                description: 'Your success is our priority. We tailor our approach to meet your unique needs.'
              }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-[#003f6d]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-[#003f6d]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 bg-[#003f6d] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Global Presence, Local Expertise</h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Headquartered in the USA and with auditors located across the globe, we provide consistent, high-quality certification services wherever you operate.
            </p>
          </div>
        </div>
      </section>

      {/* Legal Entity & Registration */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Legal Entity &amp; Registration</h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              QSC America operates as a legally registered certification body in the United States.
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Card className="border-t-4 border-[#003f6d]">
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Legal Name</p>
                    <p className="text-lg font-medium text-gray-900">Quality Standards Certification America, LLC</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Trading Name</p>
                    <p className="text-lg font-medium text-gray-900">QSC America</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Country of Incorporation</p>
                    <p className="text-lg font-medium text-gray-900">United States of America</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Established</p>
                    <p className="text-lg font-medium text-gray-900">2017</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Registered Office Address</p>
                    <p className="text-lg font-medium text-gray-900">5500 Cherokee Ave. Suite 100, Alexandria, VA 22312, USA</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Accreditation Status</p>
                    <p className="text-gray-700">Accreditation assessment in progress with the <strong>International Accreditation Service (IAS)</strong>. Certifications are currently issued under QSC America's authority as an independent certification body.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Partner with QSC America
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Contact us to discuss your certification needs and how we can support your organization.
          </p>
          <Link to="/contact">
            <Button className="bg-[#003f6d] hover:bg-[#002a4d] text-white px-8 py-6 text-lg">
              Start Your Journey Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
